import React, { useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  useTheme,
  alpha,
} from '@mui/material';
import {
  SwapHoriz,
  MoveDown,
  MoveUp,
  Timeline,
  Store,
  Business,
  LocalShipping,
  Inventory2,
  Visibility,
  Print,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { PaginatedTableColumn, PaginatedTableColumnType } from 'src/components/paginated-table';
import PaginatedTable from 'src/components/paginated-table/paginatedTable';
import { DataService } from 'src/services/data-service';
import { useAppDispatch } from 'src/redux/hooks';
import { showAlert } from 'src/redux/alert/alertReducer';
import {
  MovementRecord,
  MovementApiResponse,
  MovementDirection,
  MovementType,
  LocationType
} from 'src/types/Transfer.types';

interface ShipmentMovementTabProps {
  shipmentRefNumber: string;
  active?: boolean;
}

const MovementStatus: React.FC<{
  operationType: number;
  sourceType: number;
  destinationType: number;
}> = ({ operationType, sourceType, destinationType }) => {
  const theme = useTheme();

  const getStatusInfo = () => {
    switch (operationType) {
      case MovementType.Shipment:
        return {
          icon: <LocalShipping />,
          label: 'Shipment',
          color: theme.palette.info.main,
          bgcolor: alpha(theme.palette.info.main, 0.1)
        };
      case MovementType.Reallocation:
        return {
          icon: <Timeline />,
          label: 'Reallocation',
          color: theme.palette.secondary.main,
          bgcolor: alpha(theme.palette.secondary.main, 0.1)
        };
      case MovementType.MoveIn:
        return {
          icon: <MoveDown />,
          label: 'Move In',
          color: theme.palette.success.main,
          bgcolor: alpha(theme.palette.success.main, 0.1)
        };
      case MovementType.MoveOut:
        return {
          icon: <MoveUp />,
          label: 'Move Out',
          color: theme.palette.warning.main,
          bgcolor: alpha(theme.palette.warning.main, 0.1)
        };
      case MovementType.Transfer:
        return {
          icon: <SwapHoriz />,
          label: 'Transfer',
          color: theme.palette.primary.main,
          bgcolor: alpha(theme.palette.primary.main, 0.1)
        };
      default:
        return {
          icon: <SwapHoriz />,
          label: 'Movement',
          color: theme.palette.primary.main,
          bgcolor: alpha(theme.palette.primary.main, 0.1)
        };
    }
  };

  const statusInfo = getStatusInfo();

  return (
    <Chip
      icon={statusInfo.icon}
      label={statusInfo.label}
      size="small"
      sx={{
        color: statusInfo.color,
        bgcolor: statusInfo.bgcolor,
        fontWeight: 'medium',
        '& .MuiChip-icon': {
          color: statusInfo.color
        }
      }}
    />
  );
};

const LocationDisplay: React.FC<{
  id: string;
  name: string;
  type: number;
  variant?: 'source' | 'destination';
}> = ({ id, name, type, variant = 'source' }) => {
  if (!id || !name) return <Typography variant="body2" color="text.secondary">Unknown</Typography>;

  const getIcon = () => {
    switch (type) {
      case LocationType.StorageUnit: return <Store fontSize="small" />;
      case LocationType.Shipment: return <LocalShipping fontSize="small" />;
      case LocationType.Client: return <Business fontSize="small" />;
      case LocationType.Supplier: return <LocalShipping fontSize="small" />;
      default: return <Store fontSize="small" />;
    }
  };

  const getColor = () => {
    switch (type) {
      case LocationType.StorageUnit: return 'primary';
      case LocationType.Shipment: return 'info';
      case LocationType.Client: return 'success';
      case LocationType.Supplier: return 'warning';
      default: return 'default';
    }
  };

  const getTypeName = () => {
    switch (type) {
      case LocationType.StorageUnit: return 'Storage Unit';
      case LocationType.Shipment: return 'Shipment';
      case LocationType.Client: return 'Client';
      case LocationType.Supplier: return 'Supplier';
      default: return 'Unknown';
    }
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Chip
        icon={getIcon()}
        label={getTypeName()}
        size="small"
        color={getColor() as any}
        variant="outlined"
      />
      <Box>
        <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
          {name}
        </Typography>
      </Box>
    </Box>
  );
};

const ProductDisplay: React.FC<{
  itemName: string;
  unitBarcode: string;
  unitId: string;
}> = ({ itemName, unitBarcode, unitId }) => {
  if (!itemName) return <Typography variant="body2" color="text.secondary">Unknown Product</Typography>;

  return (
    <Box>
      <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
        {itemName}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {unitBarcode}
      </Typography>
    </Box>
  );
};

const ShipmentMovementTab: React.FC<ShipmentMovementTabProps> = ({
  shipmentRefNumber,
  active = false
}) => {
  const dispatch = useAppDispatch();

  const columns: PaginatedTableColumn[] = useMemo(() => [
    {
      id: 'timestamp',
      label: 'Date & Time',
      type: PaginatedTableColumnType.DATETIME,
      sortable: true,
      width: 180,
      render: (value: string) => (
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
            {format(new Date(value), 'MMM dd, yyyy')}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {format(new Date(value), 'HH:mm:ss')}
          </Typography>
        </Box>
      )
    },
    {
      id: 'movementType',
      label: 'Type',
      type: PaginatedTableColumnType.CUSTOM,
      width: 120,
      render: (_, row: MovementRecord) => (
        <MovementStatus
          operationType={row.operationType}
          sourceType={row.sourceType}
          destinationType={row.destinationType}
        />
      )
    },
    {
      id: 'product',
      label: 'Product',
      type: PaginatedTableColumnType.CUSTOM,
      searchable: true,
      width: 250,
      render: (_, row: MovementRecord) => (
        <ProductDisplay
          itemName={row.itemName}
          unitBarcode={row.unitBarcode}
          unitId={row.unitId}
        />
      )
    },
    {
      id: 'source',
      label: 'Source',
      type: PaginatedTableColumnType.CUSTOM,
      searchable: true,
      width: 200,
      render: (_, row: MovementRecord) => (
        <LocationDisplay
          id={row.sourceId}
          name={row.sourceName}
          type={row.sourceType}
          variant="source"
        />
      )
    },
    {
      id: 'destination',
      label: 'Destination',
      type: PaginatedTableColumnType.CUSTOM,
      searchable: true,
      width: 200,
      render: (_, row: MovementRecord) => (
        <LocationDisplay
          id={row.destinationId}
          name={row.destinationName}
          type={row.destinationType}
          variant="destination"
        />
      )
    },
    {
      id: 'quantity',
      label: 'Quantity',
      type: PaginatedTableColumnType.NUMBER,
      align: 'center',
      width: 100,
      render: (_, row: MovementRecord) => (
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
            {row.quantity}
          </Typography>
        </Box>
      )
    },
    {
      id: 'boxRefNumber',
      label: 'Box Reference',
      type: PaginatedTableColumnType.TEXT,
      width: 150,
      render: (_, row: MovementRecord) => (
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
            {row.boxRefNumber || '-'}
          </Typography>
        </Box>
      )
    }
  ], []);

  // Custom data fetch function for backend pagination
  const fetchData = useCallback(async (params: {
    page: number;
    pageSize: number;
    filters?: any;
    search?: string;
    sortBy?: string;
    sortDescending?: boolean;
  }) => {
    const response = await DataService.get(`api/transfer/history/shipment/${shipmentRefNumber}`, {
      params: {
        PageNumber: params.page,
        PageSize: params.pageSize,
        SearchTerm: params.search || '',
        SortBy: params.sortBy || '',
        SortDescending: params.sortDescending || false,
        ...params.filters
      }
    });

    if (!response.ok) {
      dispatch(showAlert({
        message: 'Failed to fetch movement data',
        type: 'error'
      }));
      throw new Error('Failed to fetch movement data');
    }

    const result: MovementApiResponse = await response.json();

    return {
      items: result.items || [],
      totalCount: result.totalCount || 0,
      pageNumber: result.pageNumber || params.page,
      pageSize: result.pageSize || params.pageSize,
      totalPages: result.totalPages || Math.ceil((result.totalCount || 0) / params.pageSize),
      hasNext: result.hasNext || false,
      hasPrevious: result.hasPrevious || false
    };
  }, [shipmentRefNumber, dispatch]);

  if (!active) {
    return null;
  }

  return (
    <Card>
      <CardContent>
        <Box sx={{ mb: 2 }}>
          <Typography variant="h6" gutterBottom>
            Movement History
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Track all item movements related to this shipment
          </Typography>
        </Box>

        <PaginatedTable
          title=""
          columns={columns}
          data={{}}
          useBackendPagination={true}
          onDataFetch={fetchData}
          cacheTimeout={300000} // 5 minutes
          selection={{ showCheckbox: false }}
          dense={true}
          stickyHeader={true}
          maxHeight="600px"
          striped={true}
          hoverable={true}
          wmsMode={true}
          densityLevel="compact"
          defaultState={{
            rowsPerPage: 10,
            order: 'desc',
            orderBy: 'timestamp'
          }}
          emptyMessage="No movement records found for this shipment"
          components={{
            EmptyState: () => (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <Timeline sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No movements found
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  No movement records found for this shipment
                </Typography>
              </Box>
            )
          }}
        />
      </CardContent>
    </Card>
  );
};

export default ShipmentMovementTab;